import React, { useState, useEffect } from 'react';
import { updatePassword } from 'aws-amplify/auth';
import { useAuth } from '../auth/AuthContext';
import { modalPrimaryBtn, modalSecondaryBtn, MODAL_BTN_ROW } from '../modalButtons';
import ModalOverlay from './ModalOverlay';

const inputStyle = {
  width: '100%',
  padding: '9px 12px',
  borderRadius: 7,
  border: '1px solid #ddd',
  fontSize: 14,
  fontFamily: 'Inter, Arial, sans-serif',
  outline: 'none',
  boxSizing: 'border-box',
  marginBottom: 10,
};

const labelStyle = {
  fontSize: 11,
  fontWeight: 600,
  color: '#888',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  display: 'block',
  marginBottom: 4,
};

const fieldValueStyle = {
  fontSize: 14,
  color: '#1a1a1a',
  padding: '9px 0',
  borderBottom: '1px solid #f0f0f0',
  marginBottom: 14,
  fontFamily: 'Inter, Arial, sans-serif',
};

const AccountModal = () => {
  const { user } = useAuth();
  const [show, setShow] = useState(false);
  const [view, setView] = useState('account'); // 'account' | 'change-password'
  const [profile, setProfile] = useState({ email: '', firstName: '', lastName: '' });

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [pwError, setPwError] = useState('');
  const [pwSuccess, setPwSuccess] = useState(false);

  useEffect(() => {
    const handle = (e) => {
      if (e.detail === 'account') {
        setProfile({
          email: localStorage.getItem('userEmail') || '',
          firstName: localStorage.getItem('userFirstName') || '',
          lastName: localStorage.getItem('userLastName') || '',
        });
        setView('account');
        setPwError('');
        setPwSuccess(false);
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        setShow(true);
      }
    };
    window.addEventListener('dashboardNav', handle);
    return () => window.removeEventListener('dashboardNav', handle);
  }, []);

  useEffect(() => {
    if (show) {
      document.body.classList.add('modal-open');
      window.__accountModalOpen = true;
    } else {
      document.body.classList.remove('modal-open');
      window.__accountModalOpen = false;
    }
    return () => {
      document.body.classList.remove('modal-open');
      window.__accountModalOpen = false;
    };
  }, [show]);

  const close = () => setShow(false);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPwError('');
    setPwSuccess(false);
    if (!currentPassword || !newPassword || !confirmPassword) {
      setPwError('All fields are required.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPwError('New passwords do not match.');
      return;
    }
    try {
      await updatePassword({ oldPassword: currentPassword, newPassword });
      setPwSuccess(true);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setPwSuccess(false);
      if (err.name === 'NotAuthorizedException') {
        setPwError('Current password is incorrect.');
      } else {
        setPwError(err.message || 'Failed to update password.');
      }
    }
  };

  if (!show) return null;

  return (
    <ModalOverlay>
      <div
        className="modal-content"
        style={{
          backgroundColor: '#fff',
          borderRadius: 12,
          padding: '28px 30px 24px',
          maxWidth: 380,
          width: '90%',
          pointerEvents: 'auto',
          textAlign: 'left',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 22 }}>
          <span style={{ fontSize: 16, fontWeight: 600, fontFamily: 'Inter, Arial, sans-serif', color: '#1a1a1a' }}>
            {view === 'account' ? 'Account' : 'Change password'}
          </span>
          <button onClick={close} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: '#999', lineHeight: 1 }}>✕</button>
        </div>

        {/* Account view */}
        {view === 'account' && (
          <>
            <label style={labelStyle}>First name</label>
            <div style={fieldValueStyle}>{profile.firstName || '—'}</div>

            <label style={labelStyle}>Last name</label>
            <div style={fieldValueStyle}>{profile.lastName || '—'}</div>

            <label style={labelStyle}>Email</label>
            <div style={{ ...fieldValueStyle, borderBottom: 'none', marginBottom: 24 }}>{profile.email || '—'}</div>

            <button
              onClick={() => { setView('change-password'); setPwError(''); setPwSuccess(false); }}
              style={{ ...modalPrimaryBtn, width: '100%' }}
            >
              Change password
            </button>
          </>
        )}

        {/* Change password view */}
        {view === 'change-password' && (
          <form onSubmit={handleChangePassword} noValidate>
            <label style={labelStyle}>Current password</label>
            <input
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              style={inputStyle}
              placeholder="Current password"
              autoComplete="current-password"
            />

            <label style={labelStyle}>New password</label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              style={inputStyle}
              placeholder="New password"
              autoComplete="new-password"
            />

            <label style={labelStyle}>Confirm new password</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              style={{ ...inputStyle, marginBottom: 14 }}
              placeholder="Confirm new password"
              autoComplete="new-password"
            />

            {pwError && <p style={{ color: '#ef4444', fontSize: 13, marginBottom: 12, fontFamily: 'Inter, Arial, sans-serif' }}>{pwError}</p>}
            {pwSuccess && <p style={{ color: '#22c55e', fontSize: 13, marginBottom: 12, fontFamily: 'Inter, Arial, sans-serif' }}>Password updated successfully.</p>}

            <div style={MODAL_BTN_ROW}>
              <button type="button" onClick={() => setView('account')} style={{ ...modalSecondaryBtn, flex: 1 }}>
                Back
              </button>
              <button type="submit" style={{ ...modalPrimaryBtn, flex: 1 }}>
                Update
              </button>
            </div>
          </form>
        )}
      </div>
    </ModalOverlay>
  );
};

export default AccountModal;
